import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {toast} from 'react-toastify';
import api from './Api';
import {IBulkStorefrontListingRequest} from '../models/BulkStorefront';
import {IActiveDestination} from '../models/Destination';
import {INormalizedProduct} from '../models/NormalizedProduct';
import {addProductToDestination, IAddProductOptions} from '../logic/addToDestination';
import {toastBody, IToastAction} from '../components/Common/ToastAction';

export interface IPendingBulkOperation {
    id: string;
    label: string;
    startedAt: number;
}

export interface IBulkOperationsState {
    pending: IPendingBulkOperation[];
}

const initialState: IBulkOperationsState = {
    pending: [],
};

const bulkOperationsSlice = createSlice({
    name: 'bulkOperations',
    initialState,
    reducers: {
        opStarted(state, action: PayloadAction<IPendingBulkOperation>) {
            state.pending.push(action.payload);
        },
        opFinished(state, action: PayloadAction<string>) {
            state.pending = state.pending.filter(op => op.id !== action.payload);
        },
    },
});

export const {opStarted, opFinished} = bulkOperationsSlice.actions;
export default bulkOperationsSlice.reducer;

/**
 * Fire-and-survive bulk storefront listing. The RTK Query mutation is dispatched at the store
 * level (not tied to any mounted component), so navigating away or unmounting the panel cannot
 * cancel it. The thunk owns the toast lifecycle and reports the resolution wherever the user is.
 */
export const runBulkListOnStorefront =
    (catalog: string, request: IBulkStorefrontListingRequest, label: string, action?: IToastAction) =>
    async (dispatch: any): Promise<void> => {
        const id = `bulk-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        dispatch(opStarted({id, label, startedAt: Date.now()}));
        const toastId = toast.loading(label);
        try {
            const result = await dispatch(
                api.endpoints.bulkListOnStorefront.initiate({catalog, request})
            ).unwrap();

            if (result.success) {
                const skipped = result.skipped ?? [];
                let render = `Storefront listing complete: ${result.updated} updated${skipped.length ? `, ${skipped.length} skipped` : ''}`;
                if (skipped.length) {
                    const reasons = skipped.slice(0, 3).map((s: {reason: string}) => s.reason).join('; ');
                    render += ` (${reasons}${skipped.length > 3 ? '…' : ''})`;
                }
                // Longer autoClose when there's a follow-up link, so it's actually clickable.
                toast.update(toastId, {
                    type: 'success',
                    render: toastBody(render, action),
                    isLoading: false,
                    autoClose: action ? 8000 : 5000,
                });
            } else {
                toast.update(toastId, {
                    type: 'error',
                    render: result.message ?? 'Failed to apply storefront listings',
                    isLoading: false,
                    autoClose: 6000,
                });
            }
        } catch {
            toast.update(toastId, {
                type: 'error',
                render: 'Failed to apply storefront listings',
                isLoading: false,
                autoClose: 5000,
            });
        } finally {
            dispatch(opFinished(id));
        }
    };

/** How many adds are in flight at once. Bundle/workspace adds are one call per product, so an
 *  unbounded fan-out over a large selection would flood the API. */
const BULK_ADD_CONCURRENCY = 5;

/**
 * Bulk add to the active destination (workspace or bundle).
 *
 * Neither has a server-side bulk endpoint the way storefront listing does, so this fans out the
 * same single-product calls the row button uses and reports one consolidated result. Registered as
 * a pending op and owning its own toast for the same reason runBulkListOnStorefront does: the run
 * must survive the user navigating away from the search results.
 */
export const runBulkAddToDestination =
    (
        destination: IActiveDestination,
        products: INormalizedProduct[],
        options?: IAddProductOptions,
        action?: IToastAction,
    ) =>
    async (dispatch: any): Promise<void> => {
        if (products.length === 0) return;

        const target = destination.name ?? destination.type;
        const label = `Adding ${products.length} product${products.length === 1 ? '' : 's'} to ${target}…`;
        const id = `bulk-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        dispatch(opStarted({id, label, startedAt: Date.now()}));
        const toastId = toast.loading(label);

        let added = 0;
        let failed = 0;
        try {
            for (let i = 0; i < products.length; i += BULK_ADD_CONCURRENCY) {
                const batch = products.slice(i, i + BULK_ADD_CONCURRENCY);
                const results = await Promise.all(
                    batch.map(p => addProductToDestination(dispatch, destination, p, options))
                );
                results.forEach(ok => (ok ? added++ : failed++));
            }

            if (failed === 0) {
                toast.update(toastId, {
                    type: 'success',
                    render: toastBody(`Added ${added} product${added === 1 ? '' : 's'} to ${target}`, action),
                    // Longer autoClose when there's a follow-up link, so it's actually clickable.
                    isLoading: false,
                    autoClose: action ? 8000 : 4000,
                });
            } else {
                // Partial success is the common failure here (one product rejected, the rest fine),
                // so report both halves rather than a bare failure. The link is still worth showing
                // as long as something landed.
                toast.update(toastId, {
                    type: added > 0 ? 'warning' : 'error',
                    render: toastBody(
                        `Added ${added} of ${products.length} to ${target} — ${failed} failed`,
                        added > 0 ? action : undefined,
                    ),
                    isLoading: false,
                    autoClose: 8000,
                });
            }
        } catch {
            toast.update(toastId, {
                type: 'error',
                render: `Failed to add products to ${target}`,
                isLoading: false,
                autoClose: 5000,
            });
        } finally {
            dispatch(opFinished(id));
        }
    };
